/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Link as ChakraLink} from '@chakra-ui/react'
import {Link as SPALink, NavLink as NavSPALink} from 'react-router-dom'
import useMultiSite from '../../hooks/use-multi-site'
import {isAbsoluteUrl} from '../../utils/url'

const Link = React.forwardRef(
    ({href, to, useNavLink = false, styles, isExternal, ...props}, ref) => {
        const _href = to || href || '/'
        const {buildUrl} = useMultiSite()

        // For external links and absolute links, render as Chakra Link
        if (isExternal || isAbsoluteUrl(_href)) {
            const updatedHref = !isAbsoluteUrl(_href) ? buildUrl(_href) : _href
            return (
                <ChakraLink
                    {...props}
                    href={updatedHref}
                    isExternal={isExternal}
                    ref={ref}
                    {...styles}
                />
            )
        }

        // For internal links, use SPA routing
        const updatedHref = buildUrl(_href)
        return (
            <ChakraLink
                as={useNavLink ? NavSPALink : SPALink}
                {...(useNavLink && {exact: true})}
                {...props}
                to={updatedHref}
                ref={ref}
                {...styles}
            />
        )
    }
)

Link.displayName = 'Link'

Link.propTypes = {
    href: PropTypes.string,
    to: PropTypes.string,
    useNavLink: PropTypes.bool,
    styles: PropTypes.any,
    isExternal: PropTypes.bool
}

export default React.memo(Link)
